import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiSave, FiTrash2, FiUser, FiSliders } from 'react-icons/fi';
import { useHubSpotPortals } from '../hooks/useHubSpotPortals';
import { attributesApi, agentAttributesApi, membersApi } from '../services/api';

export default function AgentAttributes() {
  const { memberId } = useParams();
  const navigate = useNavigate();
  const { selectedPortal } = useHubSpotPortals();
  
  const [agent, setAgent] = useState(null);
  const [attributes, setAttributes] = useState([]);
  const [agentAttributeValues, setAgentAttributeValues] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Fetch agent details and available attributes
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Fetch available attributes for the portal
      const attributesResponse = await attributesApi.fetchAttributes(selectedPortal.portal_id);
      setAttributes(attributesResponse.attributes || []);

      // Fetch agent's current attribute values
      try {
        const agentAttributesResponse = await agentAttributesApi.fetchAgentAttributes(memberId);
        setAgent(agentAttributesResponse.member);
        
        // Convert agent attributes to a map for easier lookup
        const valueMap = {};
        agentAttributesResponse.agent_attributes.forEach(attr => {
          valueMap[attr.attribute_id] = attr.attribute_value;
        });
        setAgentAttributeValues(valueMap);
      } catch (error) {
        // If no attributes are assigned yet, that's okay
        if (error.response?.status === 404) {
          // Try to get agent info from members API
          try {
            const ownersResponse = await membersApi.fetchOwners(selectedPortal.portal_id);
            const agentData = ownersResponse.owners.find(owner => owner.id === parseInt(memberId));
            if (agentData) {
              setAgent({
                id: agentData.id,
                name: `${agentData.firstName} ${agentData.lastName}`,
                email: agentData.email
              });
            }
          } catch (memberError) {
            console.error('Error fetching agent details:', memberError);
          }
          setAgentAttributeValues({});
        } else {
          throw error;
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      setError('Failed to load agent attributes. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [selectedPortal?.portal_id, memberId]);

  useEffect(() => {
    if (selectedPortal?.portal_id && memberId) {
      fetchData();
    }
  }, [selectedPortal, memberId, fetchData]);

  const handleAttributeValueChange = (attributeId, value) => {
    setAgentAttributeValues(prev => ({
      ...prev,
      [attributeId]: value
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);

    try {
      // Convert to the format expected by the API
      const attributesToUpdate = Object.entries(agentAttributeValues).map(([attributeId, value]) => ({
        attribute_id: parseInt(attributeId),
        attribute_value: value || null
      }));

      await agentAttributesApi.updateAgentAttributes(memberId, attributesToUpdate);
      alert('Agent attributes saved successfully!');
    } catch (error) {
      console.error('Error saving attributes:', error);
      setError('Failed to save attributes. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAttribute = async (attributeId) => {
    if (!window.confirm('Are you sure you want to remove this attribute from the agent?')) {
      return;
    }

    try {
      await agentAttributesApi.deleteAgentAttribute(memberId, attributeId);
      setAgentAttributeValues(prev => {
        const updated = { ...prev };
        delete updated[attributeId];
        return updated;
      });
      alert('Attribute removed successfully!');
    } catch (error) {
      console.error('Error deleting attribute:', error);
      const message = error?.response?.data?.message || error?.message || 'Failed to remove attribute. Please try again.';
      setError(message);
    }
  };

  const renderAttributeInput = (attribute) => {
    const value = agentAttributeValues[attribute.id] || '';

    switch (attribute.attribute_type) {
      case 'text':
        return (
          <textarea
            value={value}
            onChange={(e) => handleAttributeValueChange(attribute.id, e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            rows="3"
            placeholder={`Enter ${attribute.label.toLowerCase()}...`}
          />
        );

      case 'dropdown':
        return (
          <select
            value={value}
            onChange={(e) => handleAttributeValueChange(attribute.id, e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">Select {attribute.label}</option>
            {attribute.options.map(option => (
              <option key={option.id} value={option.option_value}>
                {option.option_label}
              </option>
            ))}
          </select>
        );

      case 'multiselect':
        const selectedValues = value ? value.split(',') : [];
        return (
          <div className="space-y-2 max-h-32 overflow-y-auto">
            {attribute.options.map(option => (
              <label key={option.id} className="flex items-center">
                <input
                  type="checkbox"
                  checked={selectedValues.includes(option.option_value)}
                  onChange={(e) => {
                    let newValues;
                    if (e.target.checked) {
                      newValues = [...selectedValues, option.option_value];
                    } else {
                      newValues = selectedValues.filter(v => v !== option.option_value);
                    }
                    handleAttributeValueChange(attribute.id, newValues.join(','));
                  }}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <span className="ml-2 text-sm text-gray-700">{option.option_label}</span>
              </label>
            ))}
          </div>
        );

      case 'radio_yesno':
        return (
          <div className="flex space-x-4">
            <label className="flex items-center">
              <input
                type="radio"
                name={`attribute-${attribute.id}`}
                value="yes"
                checked={value === 'yes'}
                onChange={(e) => handleAttributeValueChange(attribute.id, e.target.value)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
              />
              <span className="ml-2 text-sm text-gray-700">Yes</span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                name={`attribute-${attribute.id}`}
                value="no"
                checked={value === 'no'}
                onChange={(e) => handleAttributeValueChange(attribute.id, e.target.value)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
              />
              <span className="ml-2 text-sm text-gray-700">No</span>
            </label>
            <button
              type="button"
              onClick={() => handleAttributeValueChange(attribute.id, '')}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Clear
            </button>
          </div>
        );

      default:
        return <div className="text-gray-500">Unknown attribute type</div>;
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading agent attributes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <button
              onClick={() => navigate('/team-agents')}
              className="mr-4 p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition duration-200"
            >
              <FiArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <div className="flex items-center mb-1">
                <FiSliders className="w-6 h-6 text-gray-600 mr-3" />
                <h1 className="text-2xl font-bold text-gray-900">Agent Attributes</h1>
              </div>
              {agent && (
                <div className="flex items-center text-gray-600">
                  <FiUser className="w-4 h-4 mr-1" />
                  <span>{agent.name} ({agent.email})</span>
                </div>
              )}
            </div>
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white px-4 py-2 rounded-lg shadow-md transition-colors"
          >
            <FiSave className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
        {selectedPortal && (
          <p className="text-sm text-blue-700 mt-2">
            Portal: <span className="font-semibold">{selectedPortal.hub_domain}</span>
          </p>
        )}
      </div>

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Attributes Form */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        {attributes.length === 0 ? (
          <div className="text-center py-12">
            <FiSliders className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Attributes Available</h3>
            <p className="text-gray-600 mb-4">
              No custom attributes have been created for this portal yet.
            </p>
            <button
              onClick={() => navigate('/settings/attributes')}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
            >
              Create Attributes
            </button>
          </div>
        ) : (
          <div className="p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-6">Assign Attribute Values</h2>
            
            <div className="space-y-6">
              {attributes.map(attribute => (
                <div key={attribute.id} className="border-b border-gray-200 pb-6 last:border-b-0">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-900 mb-1">
                        {attribute.label}
                      </label>
                      <p className="text-xs text-gray-500 mb-2">
                        Type: {attribute.attribute_type === 'multiselect' ? 'Multi-select' : 
                               attribute.attribute_type === 'radio_yesno' ? 'Yes/No' : 
                               attribute.attribute_type.charAt(0).toUpperCase() + attribute.attribute_type.slice(1)}
                      </p>
                    </div>
                    {agentAttributeValues[attribute.id] && (
                      <button
                        onClick={() => handleDeleteAttribute(attribute.id)}
                        className="p-2 text-red-600 hover:text-red-800 hover:bg-red-100 rounded-lg"
                        title="Remove this attribute"
                      >
                        <FiTrash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  
                  {renderAttributeInput(attribute)}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Current Assignments Summary */}
      {attributes.length > 0 && Object.keys(agentAttributeValues).length > 0 && (
        <div className="mt-8 bg-gray-50 rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Current Attribute Assignments</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Object.entries(agentAttributeValues).map(([attributeId, value]) => {
              const attribute = attributes.find(attr => attr.id === parseInt(attributeId));
              if (!attribute || !value) return null;
              
              return (
                <div key={attributeId} className="bg-white rounded-lg p-3 border border-gray-200">
                  <div className="text-sm font-medium text-gray-900">{attribute.label}</div>
                  <div className="text-sm text-gray-600 mt-1">
                    {attribute.attribute_type === 'multiselect' ? value.split(',').join(', ') : value}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
